import {
  AdditionalTimeslotInput,
  AppointmentRecordPatientCase,
  ContactUnavailability,
  ContactUnavailabilityInput,
  CreatePatientCaseInput,
  CreatePatientInput,
  DatePrecision,
  FlexibleDateInput,
  InjuryType,
  InquiringParty,
  ParticipantContactUnavailability,
  ParticipantContactUnavailabilityInput,
  Patient,
} from "~/api/API";
import { DateISO } from "~/common/DateISO";
import { isValid, makeFlexibleDate, toStoredPair } from "~/common/PartialDate";
import { normalizeNullableId, removeTypenames } from "~/common/utils";
import {
  AdjusterInfoFormData,
  ApplicantAttorneyInfoFormData,
  AppointmentFormData,
  CarbonCopyInfoFormData,
  ClaimInfoFormData,
  DefenseAttorneyInfoFormData,
  NurseCaseManagerInfoFormData,
  PatientInfoFormData,
  TentativeAppointmentFormData,
} from "~/pages/appointments/types";

/****************************/
/***** Type Interfaces *****/
/****************************/

export interface ContactInfo {
  phone?: string | null;
  phoneExtension?: string | null;
  email?: string | null;
  fax?: string | null;
  address?: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  };
}

export interface AddressInfo {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
}

export interface PatientInfo {
  name: string;
  dateOfBirth?: Date;
  SSN?: string;
  language?: string;
  dominantHand?: string;
  email?: string;
  cellPhoneNumber?: string;
  homePhoneNumber?: string;
  homeMailingAddress: AddressInfo;
}

/****************************/
/***** Query Builders *****/
/****************************/

const sanitizeCcEmails = (
  emails?: Array<string | null>,
): string[] | undefined => {
  if (!emails) return undefined;

  return emails
    .map((email) => email?.trim())
    .filter((email): email is string => Boolean(email));
};

export { sanitizeCcEmails };

/**
 * Collects the parties opted out of scheduled comms for a case into the single
 * `scheduledCommsSuppressedParties` list stored on PatientCase (GET-3285). Each
 * party section owns its own `suppressScheduledComms` boolean in the form.
 */
export function buildScheduledCommsSuppressedParties(
  patient: PatientInfoFormData | undefined,
  adjusterInfo: AdjusterInfoFormData,
  applicantAttorney: ApplicantAttorneyInfoFormData,
  defenseAttorney: DefenseAttorneyInfoFormData,
): InquiringParty[] {
  const parties: InquiringParty[] = [];
  if (patient?.suppressScheduledComms) parties.push(InquiringParty.PATIENT);
  if (adjusterInfo.suppressScheduledComms) {
    parties.push(InquiringParty.ADJUSTER);
  }
  if (applicantAttorney.suppressScheduledComms) {
    parties.push(InquiringParty.APPLICANT_ATTORNEY);
  }
  if (defenseAttorney.suppressScheduledComms) {
    parties.push(InquiringParty.DEFENSE_ATTORNEY);
  }
  return parties;
}

export function buildContactInput(contactInfo: ContactInfo) {
  return {
    phone: contactInfo.phone || null,
    phoneExtension: contactInfo.phoneExtension || null,
    email: contactInfo.email || null,
    fax: contactInfo.fax || null,
    ...(contactInfo.address && {
      address: {
        street: contactInfo.address.street || null,
        city: contactInfo.address.city || null,
        state: contactInfo.address.state || null,
        zip: contactInfo.address.zip || null,
      },
    }),
  };
}

export function buildAddressInput(addressInfo: AddressInfo) {
  return {
    street: addressInfo.street || null,
    city: addressInfo.city || null,
    state: addressInfo.state || null,
    zip: addressInfo.zipcode || null,
  };
}

export function buildEmploymentAtInjuryInput(caseInfo: ClaimInfoFormData) {
  return {
    employerName: caseInfo?.employerAtInjuryTime || null,
    jobTitle: caseInfo?.jobTitle || null,
    contact: {
      phone: caseInfo?.employerTelephoneNumber || null,
      phoneExtension: caseInfo?.employerTelephoneNumberExtension || null,
      address: buildAddressInput(caseInfo?.companyAddress || {}),
    },
  };
}

export function buildInjuryInfoInput(
  caseInfo: ClaimInfoFormData,
  flexibleInjuryDatesEnabled = false,
) {
  const flexibleDate = (
    field: "InjuryInfo.startDateOfInjury" | "InjuryInfo.dateOfInjury",
    entered: FlexibleDateInput | null | undefined,
    exact: DateISO | null,
  ): FlexibleDateInput | null => {
    // Before capture is enabled, keep new writes migrated by mirroring the
    // exact panel/demo date. Once enabled, null is a deliberate user value and
    // must remain null rather than being silently restored from the exact date.
    const pair =
      entered ??
      (flexibleInjuryDatesEnabled
        ? null
        : makeFlexibleDate(exact?.toString() ?? null, DatePrecision.DAY));
    if (!pair || !isValid(field, pair.value ?? null, pair.precision))
      return null;
    if (
      pair.precision === DatePrecision.PRESENT &&
      caseInfo.injuryType !== InjuryType.CUMULATIVE_TRAUMA
    ) {
      return null;
    }
    return toStoredPair(pair);
  };

  return {
    injuredBodyParts: caseInfo.injuredBodyParts || null,
    claimedBodyParts: caseInfo.claimedBodyParts || null,
    description: "",
    injuryType: caseInfo.injuryType || null,
    startDateOfInjury: caseInfo.startDateOfInjury?.toString() || null,
    dateOfInjury: caseInfo.dateOfInjury?.toString() || null,
    startDateOfInjuryFlexible: flexibleDate(
      "InjuryInfo.startDateOfInjury",
      caseInfo.startDateOfInjuryFlexible,
      caseInfo.startDateOfInjury,
    ),
    dateOfInjuryFlexible: flexibleDate(
      "InjuryInfo.dateOfInjury",
      caseInfo.dateOfInjuryFlexible,
      caseInfo.dateOfInjury,
    ),
  };
}

export function buildWorkersCompInsuranceInput(
  adjusterInfo: AdjusterInfoFormData,
) {
  return {
    adjuster: adjusterInfo.adjusterName || null,
    insuranceCarrier: adjusterInfo.workCompInsuranceCarrierName || null,
    contact: buildContactInput({
      phone: adjusterInfo.adjusterPhoneNumber,
      phoneExtension: adjusterInfo.adjusterPhoneNumberExtension,
      email: adjusterInfo.adjusterEmail,
      fax: adjusterInfo.adjusterFaxNumber,
      address: buildAddressInput(adjusterInfo.insuranceCarrierAddress || {}),
    }),
  };
}

export function buildAttorneyInput(
  attorneyInfo: ApplicantAttorneyInfoFormData | DefenseAttorneyInfoFormData,
  attorneyType: "applicantAttorney" | "defenseAttorney",
) {
  if (attorneyType === "applicantAttorney") {
    const applicantAttorney = attorneyInfo as ApplicantAttorneyInfoFormData;
    return {
      name: applicantAttorney.applicantAttorneyName || null,
      lawFirmName: applicantAttorney.lawFirmName || null,
      contact: buildContactInput({
        phone: applicantAttorney.lawFirmPhoneNumber,
        phoneExtension: applicantAttorney.lawFirmPhoneNumberExtension,
        email: applicantAttorney.applicantAttorneyEmail,
        fax: applicantAttorney.lawFirmFaxNumber,
        address: buildAddressInput(applicantAttorney.lawFirmAddress || {}),
      }),
    };
  } else {
    const defenseAttorney = attorneyInfo as DefenseAttorneyInfoFormData;
    return {
      name: defenseAttorney.defenseAttorneyName || null,
      lawFirmName: defenseAttorney.lawFirmName || null,
      contact: buildContactInput({
        phone: defenseAttorney.lawFirmPhoneNumber,
        phoneExtension: defenseAttorney.lawFirmPhoneNumberExtension,
        email: defenseAttorney.defenseAttorneyEmail,
        fax: defenseAttorney.lawFirmFaxNumber,
        address: buildAddressInput(defenseAttorney.lawFirmAddress || {}),
      }),
    };
  }
}

export function buildNurseCaseManagerInput(
  nurseCaseManager: NurseCaseManagerInfoFormData,
) {
  return {
    contact: buildContactInput({
      phone: nurseCaseManager.ncmPhoneNumber,
      phoneExtension: nurseCaseManager.ncmPhoneNumberExtension,
      email: nurseCaseManager.ncmEmail,
      fax: nurseCaseManager.ncmFax,
    }),
  };
}

export function buildCCPersonInput(carbonCopy: CarbonCopyInfoFormData) {
  return {
    name: carbonCopy.ccName || null,
    contact: buildContactInput({
      phone: carbonCopy.ccPhoneNumber,
      phoneExtension: carbonCopy.ccPhoneNumberExtension,
      email: carbonCopy.ccEmail,
      address: buildAddressInput(carbonCopy.ccAddress || {}),
    }),
  };
}

export function buildCreatePatientCaseInput(
  patientID: string,
  caseInfo: ClaimInfoFormData,
  adjusterInfo: AdjusterInfoFormData,
  applicantAttorney: ApplicantAttorneyInfoFormData,
  defenseAttorney: DefenseAttorneyInfoFormData,
  nurseCaseManager: NurseCaseManagerInfoFormData,
  carbonCopy: CarbonCopyInfoFormData,
  patient?: PatientInfoFormData,
  addressManagementIds?: {
    insuranceCarrierId: string | null;
    insuranceCarrierAddressId: string | null;
    insuranceCarrierFaxNumberId?: string | null;
    insuranceCarrierPhoneNumberId?: string | null;
    applicantAttorneyLawFirmId: string | null;
    applicantAttorneyAddressId: string | null;
    applicantAttorneyFaxNumberId?: string | null;
    applicantAttorneyPhoneNumberId?: string | null;
    defenseAttorneyLawFirmId: string | null;
    defenseAttorneyAddressId: string | null;
    defenseAttorneyFaxNumberId?: string | null;
    defenseAttorneyPhoneNumberId?: string | null;
  },
  flexibleInjuryDatesEnabled = false,
): CreatePatientCaseInput {
  const result = {
    patientID,
    claimNumber: caseInfo.claimNumber || "",
    qmePanelNumber: caseInfo.panelNumber,
    WCAB: caseInfo.WCAB,
    contact: buildContactInput({
      phone: caseInfo.employerTelephoneNumber,
      phoneExtension: caseInfo.employerTelephoneNumberExtension,
      address: buildAddressInput(caseInfo.companyAddress || {}),
    }),
    employmentAtInjury: buildEmploymentAtInjuryInput(caseInfo),
    injuryInfo: buildInjuryInfoInput(caseInfo, flexibleInjuryDatesEnabled),
    workersCompInsurance: buildWorkersCompInsuranceInput(adjusterInfo),
    applicantAttorney: buildAttorneyInput(
      applicantAttorney,
      "applicantAttorney",
    ),
    defenseAttorney: buildAttorneyInput(defenseAttorney, "defenseAttorney"),
    nurseCaseManager: buildNurseCaseManagerInput(nurseCaseManager),
    ccPerson: buildCCPersonInput(carbonCopy),
    adjusterCcEmails: sanitizeCcEmails(adjusterInfo.adjusterCcEmails),
    applicantAttorneyCcEmails: sanitizeCcEmails(
      applicantAttorney.applicantAttorneyCcEmails,
    ),
    defenseAttorneyCcEmails: sanitizeCcEmails(
      defenseAttorney.defenseAttorneyCcEmails,
    ),
    scheduledCommsSuppressedParties: buildScheduledCommsSuppressedParties(
      patient,
      adjusterInfo,
      applicantAttorney,
      defenseAttorney,
    ),
    // Add the new address management IDs if provided
    ...(addressManagementIds
      ? {
          insuranceCarrierID: addressManagementIds.insuranceCarrierId,
          insuranceCarrierAddressID:
            addressManagementIds.insuranceCarrierAddressId,
          insuranceCarrierFaxNumberID:
            addressManagementIds.insuranceCarrierFaxNumberId,
          insuranceCarrierPhoneNumberID:
            addressManagementIds.insuranceCarrierPhoneNumberId,
          applicantAttorneyLawFirmID:
            addressManagementIds.applicantAttorneyLawFirmId,
          applicantAttorneyAddressID:
            addressManagementIds.applicantAttorneyAddressId,
          applicantAttorneyFaxNumberID:
            addressManagementIds.applicantAttorneyFaxNumberId,
          applicantAttorneyPhoneNumberID:
            addressManagementIds.applicantAttorneyPhoneNumberId,
          defenseAttorneyLawFirmID:
            addressManagementIds.defenseAttorneyLawFirmId,
          defenseAttorneyAddressID:
            addressManagementIds.defenseAttorneyAddressId,
          defenseAttorneyFaxNumberID:
            addressManagementIds.defenseAttorneyFaxNumberId,
          defenseAttorneyPhoneNumberID:
            addressManagementIds.defenseAttorneyPhoneNumberId,
        }
      : {}),
  };

  return result;
}

export function buildCreatePatientCaseInputFromExistingPatientCaseInput(
  patientCase: AppointmentRecordPatientCase,
  newPatientID: string,
): CreatePatientCaseInput {
  return {
    patientID: newPatientID,
    claimNumber: patientCase.patientCase.claimNumber,
    qmePanelNumber: patientCase.patientCase.qmePanelNumber,
    contact: removeTypenames(patientCase.patientCase.contact),
    employmentAtInjury: removeTypenames(
      patientCase.patientCase.employmentAtInjury,
    ),
    injuryInfo: removeTypenames(patientCase.patientCase.injuryInfo),
    workersCompInsurance: removeTypenames(
      patientCase.patientCase.workersCompInsurance,
    ),
    applicantAttorney: removeTypenames(
      patientCase.patientCase.applicantAttorney,
    ),
    defenseAttorney: removeTypenames(patientCase.patientCase.defenseAttorney),
    nurseCaseManager: removeTypenames(patientCase.patientCase.nurseCaseManager),
    ccPerson: removeTypenames(patientCase.patientCase.ccPerson),
    adjusterCcEmails: patientCase.patientCase.adjusterCcEmails ?? undefined,
    applicantAttorneyCcEmails:
      patientCase.patientCase.applicantAttorneyCcEmails ?? undefined,
    defenseAttorneyCcEmails:
      patientCase.patientCase.defenseAttorneyCcEmails ?? undefined,
    scheduledCommsSuppressedParties:
      patientCase.patientCase.scheduledCommsSuppressedParties ?? undefined,
    // v2 address management fields
    insuranceCarrierID: patientCase.patientCase.insuranceCarrierID,
    insuranceCarrierAddressID:
      patientCase.patientCase.insuranceCarrierAddressID,
    insuranceCarrierFaxNumberID:
      patientCase.patientCase.insuranceCarrierFaxNumberID,
    insuranceCarrierPhoneNumberID:
      patientCase.patientCase.insuranceCarrierPhoneNumberID,
    applicantAttorneyLawFirmID:
      patientCase.patientCase.applicantAttorneyLawFirmID,
    applicantAttorneyAddressID:
      patientCase.patientCase.applicantAttorneyAddressID,
    applicantAttorneyFaxNumberID:
      patientCase.patientCase.applicantAttorneyFaxNumberID,
    applicantAttorneyPhoneNumberID:
      patientCase.patientCase.applicantAttorneyPhoneNumberID,
    defenseAttorneyLawFirmID: patientCase.patientCase.defenseAttorneyLawFirmID,
    defenseAttorneyAddressID: patientCase.patientCase.defenseAttorneyAddressID,
    defenseAttorneyFaxNumberID:
      patientCase.patientCase.defenseAttorneyFaxNumberID,
    defenseAttorneyPhoneNumberID:
      patientCase.patientCase.defenseAttorneyPhoneNumberID,
    WCAB: patientCase.patientCase.WCAB,
  };
}

/**
 * Normalize the form's proposed-alternate timeslots into the mutation input.
 *
 * Always returns an array — `[]` propagates through to the write so the field
 * is explicitly written (and cleared in DynamoDB on update), rather than
 * collapsing to `undefined` and being omitted. Distinguishing "explicitly
 * empty" from "absent" matters for the scheduling-boundary clear.
 *
 * Shared by the legacy AppSync path (`appointment.ts`) and the ECS write
 * adapter (`appointmentApiAdapter.ts`) so both derive the field identically.
 */
export const sanitizeAdditionalTimeslots = (
  additionalTimeslots: TentativeAppointmentFormData["additionalTimeslots"],
  fallbackLocationID?: string | null,
): AdditionalTimeslotInput[] =>
  (additionalTimeslots ?? [])
    .filter(
      (
        timeslot,
      ): timeslot is {
        date: DateISO;
        time: string;
        locationID?: string | null;
      } & typeof timeslot => Boolean(timeslot?.date && timeslot?.time),
    )
    .map((timeslot) => ({
      date: timeslot.date.toString(),
      time: timeslot.time,
      locationID: normalizeNullableId(
        timeslot.locationID ?? fallbackLocationID,
      ),
    }));

const mapExistingContactUnavailability = (
  participant?: ContactUnavailability | null,
): ContactUnavailabilityInput | undefined => {
  if (!participant) {
    return undefined;
  }

  const hasEmail = typeof participant.emailUnavailable === "boolean";
  const hasPhone = typeof participant.phoneUnavailable === "boolean";

  if (!hasEmail && !hasPhone) {
    return undefined;
  }

  return {
    emailUnavailable: hasEmail ? (participant.emailUnavailable ?? null) : null,
    phoneUnavailable: hasPhone ? (participant.phoneUnavailable ?? null) : null,
  };
};

/**
 * Build the participant contact-unavailability input from the attorney/patient
 * form flags, preserving any existing adjuster unavailability (which has no
 * form control). Shared by the legacy AppSync path and the ECS write adapter.
 */
export const buildContactUnavailabilityInput = (
  formData: AppointmentFormData,
  existing?: ParticipantContactUnavailability | null,
): ParticipantContactUnavailabilityInput => {
  const applicant = formData.applicantAttorneyInfo.content;
  const defense = formData.defenseAttorneyInfo.content;

  const result: ParticipantContactUnavailabilityInput = {
    applicantAttorney: {
      emailUnavailable: Boolean(applicant.isEmailUnavailable),
      phoneUnavailable: Boolean(applicant.isPhoneUnavailable),
    },
    defenseAttorney: {
      emailUnavailable: Boolean(defense.isEmailUnavailable),
      phoneUnavailable: Boolean(defense.isPhoneUnavailable),
    },
  };

  const patient = formData.patientInfo.content;
  result.patient = {
    emailUnavailable: Boolean(patient.isEmailUnavailable),
    phoneUnavailable: Boolean(patient.isPhoneUnavailable),
  };

  const adjusterInput = mapExistingContactUnavailability(existing?.adjuster);
  if (adjusterInput) {
    result.adjuster = adjusterInput;
  }

  return result;
};

export function buildCreatePatientInputFromExistingPatientInput(
  patient: Patient,
): CreatePatientInput {
  return {
    name: patient.name,
    dateOfBirth: patient.dateOfBirth,
    SSN: patient.SSN,
    language: patient.language,
    sex: patient.sex,
    occupation: patient.occupation,
    dominantHand: patient.dominantHand,
    contact: {
      email: patient.contact?.email,
      phone: patient.contact?.phone,
      homePhoneNumber: patient.contact?.homePhoneNumber,
      address: removeTypenames(patient.contact?.address),
    },
    weightLb: patient.weightLb,
    height: removeTypenames(patient.height),
  };
}
